import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SEO from "../components/SEO";
import { confirmPasswordReset } from "../services/passwordRecoveryApi";
import "../style/AccountPage.css";

function readResetCredential(search) {
  const params = new URLSearchParams(search);
  const candidates = ["code", "token", "reset_code"];
  for (let i = 0; i < candidates.length; i += 1) {
    const key = candidates[i];
    const value = String(params.get(key) || "").trim();
    if (value) {
      return {
        value,
        field: key === "token" ? "token" : "code",
      };
    }
  }

  return {
    value: "",
    field: "code",
  };
}

export default function ResetPasswordPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const credential = useMemo(
    () => readResetCredential(location.search),
    [location.search]
  );

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(() =>
    credential.value
      ? {
          kind: "muted",
          text: "Inserisci la nuova password per completare il recupero.",
        }
      : {
          kind: "error",
          text: "Il link di reset non e valido o e incompleto. Richiedi un nuovo link.",
        }
  );

  useEffect(() => {
    setStatus(
      credential.value
        ? {
            kind: "muted",
            text: "Inserisci la nuova password per completare il recupero.",
          }
        : {
            kind: "error",
            text: "Il link di reset non e valido o e incompleto. Richiedi un nuovo link.",
          }
    );
  }, [credential.value]);

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!credential.value) {
      setStatus({
        kind: "error",
        text: "Il link di reset non e valido o e incompleto.",
      });
      return;
    }

    if (!password || !confirmPassword) {
      setStatus({
        kind: "error",
        text: "Compila entrambi i campi password.",
      });
      return;
    }
    if (password.length < 8) {
      setStatus({
        kind: "error",
        text: "La password deve avere almeno 8 caratteri.",
      });
      return;
    }
    if (password !== confirmPassword) {
      setStatus({
        kind: "error",
        text: "Le password non coincidono.",
      });
      return;
    }

    setBusy(true);
    try {
      await confirmPasswordReset({
        secret: credential.value,
        secretField: credential.field,
        password,
      });
      setStatus({
        kind: "ok",
        text: "Password aggiornata con successo. Verrai reindirizzato al login.",
      });
      setPassword("");
      setConfirmPassword("");
      window.setTimeout(() => {
        navigate("/account");
      }, 1200);
    } catch (error) {
      setStatus({
        kind: "error",
        text: error.message || "Impossibile aggiornare la password.",
      });
    } finally {
      setBusy(false);
    }
  };

  const pagePath = "/account/reset-password";
  const pageUrl = `https://kyrosgames.com${pagePath}`;

  return (
    <>
      <SEO
        title="Nuova Password | Account Kyros Games"
        description="Imposta una nuova password per il tuo account Kyros Games."
        canonical={pageUrl}
        robots="noindex, nofollow"
        structuredData={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebPage",
              "@id": `${pageUrl}#webpage`,
              url: pageUrl,
              name: "Nuova Password Account Kyros Games",
              inLanguage: "it-IT",
              isPartOf: { "@id": "https://kyrosgames.com/#website" },
              breadcrumb: { "@id": `${pageUrl}#breadcrumb` },
            },
            {
              "@type": "BreadcrumbList",
              "@id": `${pageUrl}#breadcrumb`,
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://kyrosgames.com/",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Account",
                  item: "https://kyrosgames.com/account",
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: "Nuova Password",
                  item: pageUrl,
                },
              ],
            },
          ],
        }}
      />

      <section className="account-page account-page--password-flow">
        <div className="account-page__inner">
          <header className="account-page__hero account-page__hero--auth">
            <span className="account-page__eyebrow">Secure Password Reset</span>
            <h1>Imposta una nuova password</h1>
            <p>
              Scegli una password nuova per il tuo account Kyros Games e confermala per continuare.
            </p>
          </header>

          <section className="account-page__grid">
            <article className="account-card account-card--auth account-card--password-flow">
              <div className="account-page__brand-line">
                <span className="account-page__brand-dot" aria-hidden="true" />
                <strong>Kyros Games Account</strong>
              </div>
              <h2>Nuova Password</h2>
              <p className="account-page__auth-switch">
                Vuoi richiedere un nuovo link?{" "}
                <Link to="/account/forgot-password">Reinvia reset password</Link>
              </p>

              <form
                id="resetPasswordForm"
                className="account-page__auth-form"
                noValidate
                onSubmit={onSubmit}
              >
                <div className="account-page__field-grid account-page__field-grid--single">
                  <div className="account-page__field">
                    <label htmlFor="newPassword">Nuova Password</label>
                    <input
                      id="newPassword"
                      name="newPassword"
                      type="password"
                      autoComplete="new-password"
                      required
                      minLength="8"
                      placeholder="Minimo 8 caratteri"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                    />
                  </div>
                  <div className="account-page__field">
                    <label htmlFor="newPasswordConfirm">Conferma Password</label>
                    <input
                      id="newPasswordConfirm"
                      name="newPasswordConfirm"
                      type="password"
                      autoComplete="new-password"
                      required
                      minLength="8"
                      placeholder="Ripeti la password"
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                    />
                  </div>
                </div>
                <p className="account-page__security-note">
                  Per sicurezza il codice del link e valido per circa 15 minuti.
                </p>
                <div className="actions actions--stack">
                  <button
                    id="btnResetPasswordContinue"
                    type="submit"
                    className="btn-primary"
                    disabled={busy || !credential.value}
                  >
                    {busy ? "Aggiornamento..." : "Continua"}
                  </button>
                </div>
                <div className={`status account-page__status-block ${status.kind}`}>{status.text}</div>
              </form>

              <p className="account-page__auth-inline-note">
                Dopo il reset potrai fare login da <Link to="/account">questa pagina</Link>.
              </p>
            </article>
          </section>
        </div>
      </section>
    </>
  );
}
